import React, { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { Plus, CircleX } from "lucide-react";
import { API_BACKEND_URL } from "../../../Config/Config";
import { getUserDetails } from "../../../utils/userDetailsUtils";
import { useNotification } from "../../../hooks";
import { useQueryClient } from "@tanstack/react-query";
import apiFetch from "../../../utils/apiFetch";

const TestCaseForm = ({ endpointId, open, handleCloseDialog, collections }) => {
  const queryClient = useQueryClient();
  const notify = useNotification();

  const getPathById = (id) => {
    for (const collection of collections) {
      const endpoint = collection.endpoints.find((e) => e.id === id);
      if (endpoint) {
        return endpoint.path;
      }
    }
    return null;
  };

  const [formData, setFormData] = useState({
    endpoint: "",
    testCase: "",
    httpMethod: "",
    inputParameters: "",
    expectedStatusCode: "",
    expectedResult: "",
    testType: "",
  });

  useEffect(() => {
    if (endpointId) {
      const endpoint = getPathById(endpointId);
      if (endpoint) {
        setFormData((prev) => ({ ...prev, endpoint }));
      }
    }
  }, [endpointId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    if (
      !formData.endpoint.trim() ||
      !formData.testCase.trim() ||
      !formData.httpMethod.trim() ||
      !formData.inputParameters.trim() ||
      !formData.expectedStatusCode ||
      !formData.expectedResult.trim() ||
      !formData.testType.trim()
    ) {
      notify("Please fill all required fields before submitting.", "warning");
      return;
    }
    e.preventDefault();
    try {
      const user = getUserDetails();
      const response = await apiFetch(
        `${API_BACKEND_URL}/testcase/${endpointId}?companyId=${user.companyid}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...formData, endpointId }),
        },
      );
      if (response.ok) {
        notify("Test case created successfully!", "success");
        queryClient.invalidateQueries(["latestAPIResults"]);
        handleCloseDialog();
        // Reset form
        setFormData({
          endpoint: "",
          testCase: "",
          httpMethod: "",
          inputParameters: "",
          expectedStatusCode: "",
          expectedResult: "",
          testType: "",
        });
      } else {
        throw new Error("Failed to create test case");
      }
    } catch (error) {
      console.error("Error:", error);
      notify("Failed to create test case.", "error");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseDialog}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
          boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: "1px solid #e0e0e0",
          backgroundColor: "#fafafa",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span className="font-apple flex items-center text-md text-gray-800">
          <p>Create New Test Case</p>
          <Plus className="ml-2" size={20} />
        </span>
        <button
          onClick={handleCloseDialog}
          className="flex items-center justify-center p-1.5 rounded hover:bg-gray-200 transition-all duration-200 cursor-pointer"
        >
          <CircleX size={20} className="text-red-500" />
        </button>
      </DialogTitle>

      <DialogContent
        sx={{
          marginTop: "20px",
          backgroundColor: "#ffffff",
        }}
      >
        <form onSubmit={handleSubmit} className="font-apple space-y-4">
          {/* Endpoint Display */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Endpoint <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="endpoint"
              value={formData.endpoint}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm text-gray-600"
              disabled
              placeholder="Endpoint"
            />
          </div>

          {/* Test Case Name and HTTP Method */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Test Case Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="testCase"
                value={formData.testCase}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-200 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm"
                placeholder="Enter test case name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                HTTP Method <span className="text-red-500">*</span>
              </label>
              <select
                name="httpMethod"
                value={formData.httpMethod}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-200 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm"
                required
              >
                <option value="">Select Method</option>
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
                <option value="PATCH">PATCH</option>
              </select>
            </div>
          </div>

          {/* Test Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Test Type <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="testType"
              value={formData.testType}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-200 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm"
              placeholder="Enter test type"
              required
            />
          </div>

          {/* Input Parameters */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Input Parameters <span className="text-red-500">*</span>
            </label>
            <textarea
              name="inputParameters"
              value={formData.inputParameters}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm font-mono resize-none"
              placeholder="Enter input parameters (JSON format)"
              required
            />
          </div>

          {/* Expected Result */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Expected Result <span className="text-red-500">*</span>
            </label>
            <textarea
              name="expectedResult"
              value={formData.expectedResult}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm font-mono resize-none"
              placeholder="Enter expected result (JSON format)"
              required
            />
          </div>

          {/* Expected Status Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Expected Status Code <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="expectedStatusCode"
              value={formData.expectedStatusCode}
              onChange={handleChange}
              className="w-32 px-3 py-2 border border-gray-200 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm"
              placeholder="e.g., 200, 201"
              required
            />
          </div>
        </form>
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: "1px solid #e0e0e0",
          backgroundColor: "#fafafa",
          gap: "12px",
        }}
      >
        <button
          type="button"
          onClick={handleCloseDialog}
          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-all duration-200 cursor-pointer"
        >
          Cancel
        </button>
        <button
          type="submit"
          onClick={handleSubmit}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-all duration-200 text-sm cursor-pointer"
        >
          <Plus size={16} className="mr-2" />
          Create Test Case
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default TestCaseForm;
